import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { COLORS } from '../../../utils/colors';

interface CheckboxOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface SingleCheckboxProps {
  isChecked: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  color?: 'blue' | 'green' | 'red' | 'purple' | 'orange';
  className?: string;
  defaultColor?: string;
  indeterminate?: boolean;
}

interface MultiCheckboxProps {
  options: CheckboxOption[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  color?: 'blue' | 'green' | 'red' | 'purple' | 'orange';
  direction?: 'horizontal' | 'vertical';
  className?: string;
}

type DillCheckboxProps = SingleCheckboxProps | MultiCheckboxProps;

const colorMap: Record<string, string> = {
  blue: COLORS.PRIMARY_500,
  green: '#16A34A',
  red: '#DC2626',
  purple: '#9333EA',
  orange: '#EA580C',
};

const CHECKMARK_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.207 4.793a1 1 0 0 1 0 1.414l-5 5a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L6.5 9.086l4.293-4.293a1 1 0 0 1 1.414 0z'/%3E%3C/svg%3E")`;
const INDETERMINATE_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='3' y='7' width='10' height='2' rx='1'/%3E%3C/svg%3E")`;

const SingleCheckbox = ({
  isChecked,
  onChange,
  disabled,
  color = 'blue',
  className,
  defaultColor,
  indeterminate,
}: SingleCheckboxProps) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current && typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !isChecked && indeterminate;
    }
  }, [ref, indeterminate, isChecked]);

  const checkedColor = colorMap[color] || defaultColor || colorMap.blue;
  const isIndeterminate = !isChecked && indeterminate;
  const isActive = isChecked || isIndeterminate;

  return (
    <input
      type="checkbox"
      ref={ref}
      checked={isChecked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked)}
      className={twMerge(
        'w-4 h-4 cursor-pointer shrink-0 appearance-none border border-gray-300 rounded-[4px]',
        className
      )}
      style={{
        backgroundColor: isActive ? checkedColor : 'white',
        borderColor: isActive ? checkedColor : undefined,
        backgroundImage: isChecked ? CHECKMARK_SVG : isIndeterminate ? INDETERMINATE_SVG : 'none',
      }}
    />
  );
};

const MultiCheckbox = ({
  options,
  value,
  onChange,
  disabled,
  color = 'blue',
  direction = 'vertical',
  className,
}: MultiCheckboxProps) => {
  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const checkedColor = colorMap[color] || colorMap.blue;
  const containerClass =
    direction === 'horizontal' ? 'flex flex-wrap gap-4' : 'flex flex-col gap-2';

  return (
    <div className={twMerge(containerClass, className)}>
      {options.map((option) => {
        const isChecked = value.includes(option.value);
        return (
          <label
            key={option.value}
            className={twMerge(
              'flex items-center gap-2 cursor-pointer',
              option.disabled && 'opacity-50 cursor-not-allowed'
            )}>
            <input
              type="checkbox"
              checked={isChecked}
              disabled={disabled || option.disabled}
              onChange={() => !option.disabled && handleToggle(option.value)}
              className="w-4 h-4 cursor-pointer shrink-0 appearance-none border border-gray-300 rounded-sm"
              style={{
                backgroundColor: isChecked ? checkedColor : 'white',
                borderColor: isChecked ? checkedColor : undefined,
                backgroundImage: isChecked ? CHECKMARK_SVG : 'none',
              }}
            />
            <span className="text-sm text-gray-700">{option.label}</span>
          </label>
        );
      })}
    </div>
  );
};

export const DillCheckbox = (props: DillCheckboxProps) => {
  if ('options' in props) {
    return <MultiCheckbox {...props} />;
  }
  return <SingleCheckbox {...props} />;
};
